import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Button from "app/popleads/components/ui-kit/Button";

import styles from "./AssistantDrawer.module.scss";

/* How "Show less" times the rows leaving, matching `.suggestionListOut` in the stylesheet: each row
   this much after the one below it, each fade this long. */
const ROW_OUT_STAGGER_MS = 8;
const ROW_OUT_MS = 140;

/* The empty window opens on the user's name rather than a description of the assistant — the
   suggestions under the composer already say what it is for. */
export const greetingFor = (name) => {
    const hour = new Date().getHours();
    const part = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
    const firstName = String(name || "")
        .trim()
        .split(/\s+/)[0];
    return firstName ? `Good ${part}, ${firstName}` : `Good ${part}`;
};

/**
 * Nothing asked yet: a greeting, the composer, and the suggestions as chips under it — the way
 * ChatGPT opens. One block, shared by the CRM window and the extension's panel, so both open the
 * same way and "More" behaves the same in both.
 *
 * Five chips and "More" at first. "More" turns them into a two-column list of every suggestion
 * rather than adding chips: chips of uneven width wrap into ragged rows with one left on its own,
 * and a grid does not. "Show less" fills the last cell. A new chat opens on the five again — this
 * block is only mounted while the thread is empty, so its state starts over each time.
 *
 * `composer` is the shell's own composer, drawn in the middle here and docked by the shell once a
 * question has been asked.
 */
const AssistantEmptyState = ({ name, composer, commands, moreCommands, showCommands, runCommand, sendMessage }) => {
    /* Whether "More" has turned the five into the list of every suggestion, and whether the five are
       back from it — only then do they fade in; on opening they simply appear with the window. */
    const [showMore, setShowMore] = useState(false);
    const [isBackFromMore, setIsBackFromMore] = useState(false);

    /* The block, and where it stood before "More" or "Show less" changed its height. It is centred,
       so a change in height moves it, and that move is animated from the old place to the new one
       (see the layout effect below). */
    const blockRef = useRef(null);
    const blockTopRef = useRef(null);

    const toggleMore = (next) => {
        if (blockRef.current) blockTopRef.current = blockRef.current.getBoundingClientRect().top;
        setShowMore(next);
        setIsBackFromMore(!next);
    };

    /* "Show less" is "More" played backwards: the rows leave last first, and only then does the list
       give way to the five and the block slide down. The wait is the stylesheet's — the last row's
       delay plus its fade — so the two have to change together. */
    const [isClosingMore, setIsClosingMore] = useState(false);
    const closeMoreTimerRef = useRef(null);
    const suggestionCount = commands.length + moreCommands.length + 1;

    const closeMore = () => {
        if (isClosingMore) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            toggleMore(false);
            return;
        }
        setIsClosingMore(true);
        closeMoreTimerRef.current = setTimeout(() => {
            closeMoreTimerRef.current = null;
            setIsClosingMore(false);
            toggleMore(false);
        }, (suggestionCount - 1) * ROW_OUT_STAGGER_MS + ROW_OUT_MS);
    };

    useEffect(() => () => clearTimeout(closeMoreTimerRef.current), []);

    /**
     * The block slides to where the new height puts it instead of jumping there. Measured after
     * React has laid the change out and before the browser paints it: the block is pushed back by
     * the distance it moved, then let go with a transition, so the first frame painted is the old
     * position. A layout effect, not an ordinary one — after a paint the jump would already show.
     */
    useLayoutEffect(() => {
        const el = blockRef.current;
        const from = blockTopRef.current;
        blockTopRef.current = null;
        if (!el || from === null) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

        const distance = from - el.getBoundingClientRect().top;
        if (Math.abs(distance) < 1) return;

        el.style.transition = "none";
        el.style.transform = `translateY(${distance}px)`;
        el.getBoundingClientRect(); // commit the starting point before the transition is set
        el.style.transition = "transform 0.36s cubic-bezier(0.16, 1, 0.3, 1)";
        el.style.transform = "";
    }, [showMore]);

    /* One suggestion chip. The label is its own element so a long one can be cut short on a phone
       instead of running past the edge of the window. */
    const suggestionChip = (command, onClick, className = "") => (
        <Button
            key={command.label}
            variant="neutral"
            buttonStyle="option"
            size="small"
            leftIcon={command.icon}
            className={`${styles.suggestion} ${className}`}
            onClick={onClick}
        >
            <span className={styles.suggestionLabel}>{command.label}</span>
        </Button>
    );

    /* The same suggestion as a row of the expanded list: full width, left-aligned. `--row` is its
       place in the list, which the stylesheet turns into a delay so the rows arrive one after
       another rather than all at once. */
    const suggestionRow = (command, index, onClick, className = "") => (
        <Button
            key={command.label}
            variant="neutral"
            buttonStyle="borderless"
            size="small"
            leftIcon={command.icon}
            className={`${styles.suggestionRow} ${className}`}
            style={{ "--row": index }}
            onClick={onClick}
        >
            <span className={styles.suggestionLabel}>{command.label}</span>
        </Button>
    );

    return (
        <div className={styles.paletteColumn} ref={blockRef}>
            <h3 className={styles.introTitle}>{greetingFor(name)}</h3>
            <div className={styles.emptyComposer}>{composer}</div>
            {showCommands && !showMore && (
                <div className={`${styles.suggestions} ${isBackFromMore ? styles.suggestionsIn : ""}`}>
                    {commands.map((command, index) => suggestionChip(command, () => runCommand(index)))}
                    {moreCommands.length > 0 &&
                        suggestionChip({ label: "More" }, () => toggleMore(true), styles.suggestionMore)}
                </div>
            )}
            {showCommands && showMore && (
                <div
                    className={`${styles.suggestionList} ${isClosingMore ? styles.suggestionListOut : ""}`}
                    style={{ "--rows": suggestionCount }}
                >
                    {commands.map((command, index) => suggestionRow(command, index, () => runCommand(index)))}
                    {moreCommands.map((command, index) =>
                        suggestionRow(command, commands.length + index, () => sendMessage(command.label))
                    )}
                    {suggestionRow(
                        { icon: "chevron-up", label: "Show less" },
                        commands.length + moreCommands.length,
                        closeMore,
                        styles.suggestionRowLess
                    )}
                </div>
            )}
        </div>
    );
};

export default AssistantEmptyState;
